/**
 * One linked group row: order handle (buttons — a two-item list rarely
 * needs dnd), chip, live match count, how its drops use the lists while
 * the card is folded, list-order mode segment, the fold (23/10/2026 — the
 * whole card), re-pick (21/10/2026), edit, remove. FORMULA (Phase 18) ranks the lists on their own results.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, X } from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { isDynamicListGroupRule } from '@trafficpulse/shared';

import type { ListGroup } from '@/features/list-groups';
import { GroupChip } from '../../shared/GroupChip';

// "Biggest first" (INHERIT) is hidden for now (user, 14/10/2026: "we dont need it now, but in the future") —
// "My order" with nothing dragged yet IS the biggest-first order, so no plan changes. Restore the line to bring it back.
const MODES = [
  // { value: 'INHERIT', label: 'Biggest first', hint: 'Largest lead count first — the default order.' },
  { value: 'MANUAL', label: 'My order', hint: 'Drag the lists below into the order the day should consume them — until you do, the biggest list goes first.' },
  { value: 'FORMULA', label: 'Formula', hint: 'Rank the lists on a formula of their own results on the tracker — best first; a list with too little traffic keeps its biggest-first place after the ranked ones.' },
] as const;

/** A dynamic group's lists change by themselves — only a formula can rank them (user, 19/10/2026). */
/** 21/10/2026 — a group stays on the list it is on until it burns or runs dry; this forgets that list, burns kept. */
const REPICK_HINT =
  "Re-pick the list: the list this container's group is on is forgotten (its burned lists stay burned) and the slots show the first list of the order as it is now. A day already laid out re-syncs — its unsent drafts move. A saved rule, order or formula does this by itself, and so do Generate now and the daily auto-generate for a formula group.";

const DYNAMIC_HINT =
  'The lists of this group change by themselves (its rule uses tags, names or metrics), so there is no order of your own to keep — the lists are ranked by a formula on their results, best first.';

interface Props {
  group: ListGroup | undefined;
  listGroupId: string;
  orderMode: string;
  /** The card is unfolded (23/10/2026: each group card folds on its own). */
  open: boolean;
  /** Folded: how its drops use the lists ("One list", "Every list · best 10"). */
  summary?: string;
  isFirst: boolean;
  isLast: boolean;
  onSetOrderMode: (mode: string) => void;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  /** Opens the group's editor in a pop-up on this page; absent while the group is loading. */
  onEdit?: () => void;
  /** 21/10/2026 — Re-pick the list; absent on a container not saved yet (no list picked). */
  onRepick?: () => void;
  repicking?: boolean;
}

export const GroupPickRow: FC<Props> = (p) => {
  const dynamic = isDynamicListGroupRule(p.group?.ruleJson);
  const modes = dynamic ? MODES.filter((m) => m.value === 'FORMULA') : MODES;
  return (
  <div className="flex items-center gap-3 px-3 py-2">
    <span className="flex flex-col">
      <button type="button" aria-label="Move up" disabled={p.isFirst} onClick={p.onMoveUp} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" aria-label="Move down" disabled={p.isLast} onClick={p.onMoveDown} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </span>
    <GroupChip name={p.group?.name ?? p.listGroupId} color={p.group?.color ?? '#64748b'} />
    <span className="text-xs text-muted-foreground">
      {p.group?.matchCount != null ? `${p.group.matchCount} lists match` : ''}
    </span>
    {!p.open && p.summary ? (
      <span className="truncate text-xs font-medium text-foreground/80" data-testid="group-summary">
        {p.summary}
      </span>
    ) : null}
    <span className="grow" />
    {dynamic ? (
      <Tooltip content={DYNAMIC_HINT}>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground" data-testid="dynamic-group">
          dynamic lists
        </span>
      </Tooltip>
    ) : null}
    <span className="flex items-center gap-0.5 rounded-md border border-border/60 bg-muted/30 p-0.5">
      {modes.map((mode) => (
        <Tooltip key={mode.value} content={dynamic ? DYNAMIC_HINT : mode.hint}>
          <button
            type="button"
            onClick={() => p.onSetOrderMode(mode.value)}
            aria-pressed={p.orderMode === mode.value}
            className={cn(
              'rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
              p.orderMode === mode.value
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {mode.label}
          </button>
        </Tooltip>
      ))}
    </span>
    <button
      type="button"
      aria-label={p.open ? 'Fold the group' : 'Unfold the group'}
      aria-expanded={p.open}
      onClick={p.onToggle}
      className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <ChevronDown className={cn('h-4 w-4', p.open && 'rotate-180')} />
    </button>
    {p.onRepick ? (
      <Tooltip content={REPICK_HINT}>
        <button
          type="button"
          aria-label="Re-pick the list"
          disabled={p.repicking}
          onClick={p.onRepick}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', p.repicking && 'animate-spin')} />
        </button>
      </Tooltip>
    ) : null}
    <button
      type="button"
      onClick={p.onEdit}
      disabled={!p.onEdit}
      className="text-xs font-medium text-primary hover:underline disabled:opacity-40"
    >
      Edit rule
    </button>
    <button
      type="button"
      aria-label="Remove group"
      onClick={p.onRemove}
      className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
  );
};
